import React, { useState, useCallback, useEffect } from "react";
import { Accordion, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import FirebaseStorageService from "src/services/Firebase/storage";
import LoadingContainer from "src/components/LoadingContainer";
import { generateAndDownloadRFQPdf } from "src/utils/pdfGenerator";
import {
  RequestQuoteFormData,
  BillToData,
  ShipToData,
  LaborDetailRow,
  ServiceDetailRow,
  EquipmentDetailRow,
  MaterialDetailRow,
  AdditionalFeeRow,
} from "./types";
import {
  FormContainer,
  SectionContainer,
  SectionTitle,
  InputRow,
  InputFieldWrapper,
  CheckboxWrapper,
  RadioButtonGroup,
  RadioButtonWrapper,
  AccordionContainer,
  TableContainer,
  StyledTable,
  TotalRow,
  AddRowButton,
  RemoveRowButton,
  QuoteTotalContainer,
  FileInputWrapper,
  SubmitButtonContainer,
} from "./styles";
import Dropdown from "src/components/Dropdown";
import { DropdownItem } from "src/components/Dropdown/types";

interface RequestQuoteFormProps {
  onCancel?: () => void;
  initialData?: Partial<RequestQuoteFormData>;
}

const defaultFormValues: RequestQuoteFormData = {
  billTo: { attentionTo: "", client: "", address: "" },
  shipTo: { sameAsAddress: false, attentionTo: "", client: "", address: "" },
  quotationDescription: { description: "", vendorName: "" },
  laborDetails: [],
  laborTotalAmount: 0,
  serviceDetails: [],
  serviceTotalAmount: 0,
  equipmentDetails: [],
  equipmentTotalAmount: 0,
  materialDetails: [],
  materialTotalAmount: 0,
  additionalFees: [],
  additionalFeesTotalAmount: 0,
  quoteTotalAmount: 0,
  scopeOfWork: { scopeOfWork: "", attachment: null, markupPercentage: 0 },
  quoteNextStep: "",
};

const RequestQuoteFormComponent: React.FC<RequestQuoteFormProps> = ({ onCancel, initialData }) => {
  const { id: wonum } = useParams<{ id: string }>();
  // const navigate = useNavigate();
  const { id: clientUserId, isManager } = useSelector(getUserData);
  const [isLoading, setIsLoading] = useState(true);

  const [allVendors, setAllVendors] = useState<DropdownItem[]>([]);

  const { handleSubmit, watch, setValue } = useForm<RequestQuoteFormData>({
    defaultValues: {
      ...defaultFormValues,
      ...initialData,
      billTo: {
        ...defaultFormValues.billTo,
        ...(initialData?.billTo || {}),
      },
      shipTo: {
        ...defaultFormValues.shipTo,
        ...(initialData?.shipTo || {}),
      },
      quotationDescription: {
        ...defaultFormValues.quotationDescription,
        ...(initialData?.quotationDescription || {}),
      },
      scopeOfWork: {
        ...defaultFormValues.scopeOfWork,
        ...(initialData?.scopeOfWork || {}),
      },
    },
  });

  const formData = watch();
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  const fetchAllVendors = useCallback(async () => {
    try {
      const vendors = await FirebaseDataService.getEmployeeVendors(clientUserId);
      const vendorArray = Array.isArray(vendors) ? vendors : vendors?.data || [];
      const vendorOptions = vendorArray.map((vendor) => ({
        id: vendor.id,
        // name: vendor.name,
        value: vendor.name,
      }));
      setAllVendors(vendorOptions);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  }, [clientUserId]);

  // Fetch work order data on component mount
  useEffect(() => {
    const fetchWorkOrderData = async () => {
      try {
        setIsLoading(true);
        if (wonum) {
          const { data: viewTicketData } = await FirebaseDataService.getDataForTicketViewForm(
            wonum,
            clientUserId,
            isManager,
          );

          if (viewTicketData?.workOrder) {
            const { workOrder } = viewTicketData;
            const clientName = workOrder.pluspcustomer?.name || "";
            const clientAddress = (workOrder.pluspcustomer as { address?: string } | undefined)?.address || "";
            const locationLabel = workOrder.locationData?.label || "";
            const locationAddress = workOrder.locationData?.address || "";
            const hasInitialBillTo =
              !!initialData?.billTo?.client || !!initialData?.billTo?.address || !!initialData?.billTo?.attentionTo;
            const hasInitialShipTo =
              !!initialData?.shipTo?.client || !!initialData?.shipTo?.address || !!initialData?.shipTo?.attentionTo;

            if (!hasInitialBillTo) {
              setValue("billTo", {
                attentionTo: "",
                client: clientName,
                address: clientAddress || locationAddress || "",
              });
            }

            if (!hasInitialShipTo) {
              setValue("shipTo", {
                sameAsAddress: false,
                attentionTo: "",
                client: locationLabel,
                address: locationAddress || "",
              });
            }
          }
        }

        await fetchAllVendors();
      } catch (error) {
        console.error("Error fetching work order data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkOrderData();
  }, [wonum, clientUserId, isManager, setValue, initialData, fetchAllVendors]);

  // Bill To Section
  const handleBillToChange = useCallback(
    (field: keyof BillToData, value: string) => {
      setValue("billTo", { ...formData.billTo, [field]: value });
    },
    [formData, setValue],
  );

  // Ship To Section
  const handleShipToChange = useCallback(
    (field: keyof ShipToData, value: string | boolean) => {
      const newShipTo = { ...formData.shipTo, [field]: value };
      if (field === "sameAsAddress" && value === true) {
        newShipTo.attentionTo = formData.billTo.attentionTo;
        newShipTo.client = formData.billTo.client;
        newShipTo.address = formData.billTo.address;
      }
      setValue("shipTo", newShipTo);
    },
    [formData, setValue],
  );

  // Labor Details Table Functions
  const addLaborRow = useCallback(() => {
    const newRow: LaborDetailRow = {
      id: uuidv4(),
      servicePerMonth: 0,
      rate: 0,
      total: 0,
    };
    setValue("laborDetails", [...(formData.laborDetails || []), newRow]);
  }, [formData, setValue]);

  const updateLaborRow = useCallback(
    (id: string, field: keyof Omit<LaborDetailRow, "id">, value: number) => {
      const updatedDetails = (formData.laborDetails || []).map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === "rate" || field === "servicePerMonth") {
            updatedRow.total = updatedRow.servicePerMonth * updatedRow.rate;
          }
          return updatedRow;
        }
        return row;
      });
      const laborTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("laborDetails", updatedDetails);
      setValue("laborTotalAmount", laborTotal);
      updateQuoteTotal(
        laborTotal,
        formData.serviceTotalAmount,
        formData.equipmentTotalAmount,
        formData.materialTotalAmount,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  const removeLaborRow = useCallback(
    (id: string) => {
      const updatedDetails = (formData.laborDetails || []).filter((row) => row.id !== id);
      const laborTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("laborDetails", updatedDetails);
      setValue("laborTotalAmount", laborTotal);
      updateQuoteTotal(
        laborTotal,
        formData.serviceTotalAmount,
        formData.equipmentTotalAmount,
        formData.materialTotalAmount,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  // Service Details Table Functions
  const addServiceRow = useCallback(() => {
    const newRow: ServiceDetailRow = {
      id: uuidv4(),
      service: "",
      rate: 0,
      quantity: 0,
      total: 0,
    };
    setValue("serviceDetails", [...(formData.serviceDetails || []), newRow]);
  }, [formData, setValue]);

  const updateServiceRow = useCallback(
    (id: string, field: keyof Omit<ServiceDetailRow, "id">, value: string | number) => {
      const updatedDetails = (formData.serviceDetails || []).map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === "rate" || field === "quantity") {
            updatedRow.total = updatedRow.quantity * updatedRow.rate;
          }
          return updatedRow;
        }
        return row;
      });
      const serviceTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("serviceDetails", updatedDetails);
      setValue("serviceTotalAmount", serviceTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        serviceTotal,
        formData.equipmentTotalAmount,
        formData.materialTotalAmount,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  const removeServiceRow = useCallback(
    (id: string) => {
      const updatedDetails = (formData.serviceDetails || []).filter((row) => row.id !== id);
      const serviceTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("serviceDetails", updatedDetails);
      setValue("serviceTotalAmount", serviceTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        serviceTotal,
        formData.equipmentTotalAmount,
        formData.materialTotalAmount,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  // Equipment Details Table Functions
  const addEquipmentRow = useCallback(() => {
    const newRow: EquipmentDetailRow = {
      id: uuidv4(),
      equipment: "",
      rate: 0,
      quantity: 0,
      total: 0,
    };
    setValue("equipmentDetails", [...(formData.equipmentDetails || []), newRow]);
  }, [formData, setValue]);

  const updateEquipmentRow = useCallback(
    (id: string, field: keyof Omit<EquipmentDetailRow, "id">, value: string | number) => {
      const updatedDetails = (formData.equipmentDetails || []).map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === "rate" || field === "quantity") {
            updatedRow.total = updatedRow.quantity * updatedRow.rate;
          }
          return updatedRow;
        }
        return row;
      });
      const equipmentTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("equipmentDetails", updatedDetails);
      setValue("equipmentTotalAmount", equipmentTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        formData.serviceTotalAmount,
        equipmentTotal,
        formData.materialTotalAmount,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  const removeEquipmentRow = useCallback(
    (id: string) => {
      const updatedDetails = (formData.equipmentDetails || []).filter((row) => row.id !== id);
      const equipmentTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("equipmentDetails", updatedDetails);
      setValue("equipmentTotalAmount", equipmentTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        formData.serviceTotalAmount,
        equipmentTotal,
        formData.materialTotalAmount,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  // Material Details Table Functions
  const addMaterialRow = useCallback(() => {
    const newRow: MaterialDetailRow = {
      id: uuidv4(),
      material: "",
      rate: 0,
      quantity: 0,
      total: 0,
    };
    setValue("materialDetails", [...(formData.materialDetails || []), newRow]);
  }, [formData, setValue]);

  const updateMaterialRow = useCallback(
    (id: string, field: keyof Omit<MaterialDetailRow, "id">, value: string | number) => {
      const updatedDetails = (formData.materialDetails || []).map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === "rate" || field === "quantity") {
            updatedRow.total = updatedRow.quantity * updatedRow.rate;
          }
          return updatedRow;
        }
        return row;
      });
      const materialTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("materialDetails", updatedDetails);
      setValue("materialTotalAmount", materialTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        formData.serviceTotalAmount,
        formData.equipmentTotalAmount,
        materialTotal,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  const removeMaterialRow = useCallback(
    (id: string) => {
      const updatedDetails = (formData.materialDetails || []).filter((row) => row.id !== id);
      const materialTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("materialDetails", updatedDetails);
      setValue("materialTotalAmount", materialTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        formData.serviceTotalAmount,
        formData.equipmentTotalAmount,
        materialTotal,
        formData.additionalFeesTotalAmount,
      );
    },
    [formData, setValue],
  );

  // Additional Fees Table Functions
  const addAdditionalFeeRow = useCallback(() => {
    const newRow: AdditionalFeeRow = {
      id: uuidv4(),
      typeOfFee: "",
      rate: 0,
      quantity: 0,
      total: 0,
    };
    setValue("additionalFees", [...(formData.additionalFees || []), newRow]);
  }, [formData, setValue]);

  const updateAdditionalFeeRow = useCallback(
    (id: string, field: keyof Omit<AdditionalFeeRow, "id">, value: string | number) => {
      const updatedDetails = (formData.additionalFees || []).map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === "rate" || field === "quantity") {
            updatedRow.total = updatedRow.quantity * updatedRow.rate;
          }
          return updatedRow;
        }
        return row;
      });
      const feesTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("additionalFees", updatedDetails);
      setValue("additionalFeesTotalAmount", feesTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        formData.serviceTotalAmount,
        formData.equipmentTotalAmount,
        formData.materialTotalAmount,
        feesTotal,
      );
    },
    [formData, setValue],
  );

  const removeAdditionalFeeRow = useCallback(
    (id: string) => {
      const updatedDetails = (formData.additionalFees || []).filter((row) => row.id !== id);
      const feesTotal = updatedDetails.reduce((sum, row) => sum + row.total, 0);
      setValue("additionalFees", updatedDetails);
      setValue("additionalFeesTotalAmount", feesTotal);
      updateQuoteTotal(
        formData.laborTotalAmount,
        formData.serviceTotalAmount,
        formData.equipmentTotalAmount,
        formData.materialTotalAmount,
        feesTotal,
      );
    },
    [formData, setValue],
  );

  // Update quote total
  const updateQuoteTotal = useCallback(
    (labor: number, service: number, equipment: number, material: number, fees: number) => {
      const total = labor + service + equipment + material + fees;
      setValue("quoteTotalAmount", total);
    },
    [setValue],
  );

  // Handle file attachment
  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAttachmentFile(file);
    setValue("scopeOfWork", { ...formData.scopeOfWork, attachment: file });
  };

  // Handle form submission
  const handleFormSubmit = async (data: RequestQuoteFormData) => {
    try {
      setIsLoading(true);

      // Prepare the data to send
      let dataToSend = { ...data };

      // Upload attachment if present
      if (attachmentFile) {
        const timestamp = new Date().getTime();
        const fileName = `${timestamp}_${attachmentFile.name}`;
        const folderPath = `rfq_attachments/${clientUserId}`;

        const uploadResult = await FirebaseStorageService.uploadImageToStorage(attachmentFile, folderPath, fileName);

        if (uploadResult && uploadResult.success && uploadResult.data) {
          // Set the attachment URL in the data
          dataToSend = {
            ...dataToSend,
            scopeOfWork: {
              ...dataToSend.scopeOfWork,
              attachment: uploadResult.data, // Send the download URL instead of File object
            },
          };
        } else {
          console.error("Failed to upload attachment:", uploadResult?.error);
          // Continue with the submission even if file upload fails
        }
      } else {
        // If no attachment, set attachment to empty string
        dataToSend = {
          ...dataToSend,
          scopeOfWork: {
            ...dataToSend.scopeOfWork,
            attachment: "",
          },
        };
      }

      const response = await FirebaseDataService.createWorkOrderRfq(clientUserId, isManager, dataToSend);
      console.log("RFQ created successfully:", response);

      // Generate and download PDF after successful API call
      try {
        await generateAndDownloadRFQPdf(data, `RFQ_${wonum || "Quote"}`);
        console.log("PDF downloaded successfully");
      } catch (pdfError) {
        console.error("Error generating PDF:", pdfError);
        // Show error message but don't prevent the form from succeeding
        alert(
          "RFQ submitted successfully, but there was an issue generating the PDF. You can try downloading it again from your account.",
        );
      }
    } catch (error) {
      console.error("Error creating RFQ:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      <FormContainer>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Bill To Section */}
          <SectionContainer>
            <SectionTitle>Bill To</SectionTitle>
            <InputRow>
              <InputFieldWrapper>
                <label htmlFor="billTo-attentionTo">Attention To</label>
                <input
                  id="billTo-attentionTo"
                  type="text"
                  value={formData.billTo.attentionTo}
                  onChange={(e) => handleBillToChange("attentionTo", e.target.value)}
                  placeholder="Enter attention to"
                />
              </InputFieldWrapper>
              <InputFieldWrapper>
                <label htmlFor="billTo-client">Client / Location</label>
                <input
                  id="billTo-client"
                  type="text"
                  value={formData.billTo.client}
                  onChange={(e) => handleBillToChange("client", e.target.value)}
                  placeholder="Enter client and location"
                />
              </InputFieldWrapper>
            </InputRow>
            <InputRow>
              <InputFieldWrapper style={{ flex: 1 }}>
                <label htmlFor="billTo-address">Address</label>
                <textarea
                  id="billTo-address"
                  value={formData.billTo.address}
                  onChange={(e) => handleBillToChange("address", e.target.value)}
                  placeholder="Enter address"
                />
              </InputFieldWrapper>
            </InputRow>
          </SectionContainer>

          {/* Ship To Section */}
          <SectionContainer>
            <SectionTitle>Ship To</SectionTitle>
            <CheckboxWrapper>
              <input
                type="checkbox"
                id="shipTo-sameAsAddress"
                checked={formData.shipTo.sameAsAddress}
                onChange={(e) => handleShipToChange("sameAsAddress", e.target.checked)}
              />
              <label htmlFor="shipTo-sameAsAddress">Same as Address</label>
            </CheckboxWrapper>
            <InputRow>
              <InputFieldWrapper>
                <label htmlFor="shipTo-attentionTo">Attention To</label>
                <input
                  id="shipTo-attentionTo"
                  type="text"
                  value={formData.shipTo.attentionTo}
                  onChange={(e) => handleShipToChange("attentionTo", e.target.value)}
                  placeholder="Enter attention to"
                  disabled={formData.shipTo.sameAsAddress}
                />
              </InputFieldWrapper>
              <InputFieldWrapper>
                <label htmlFor="shipTo-client">Client / Location</label>
                <input
                  id="shipTo-client"
                  type="text"
                  value={formData.shipTo.client}
                  onChange={(e) => handleShipToChange("client", e.target.value)}
                  placeholder="Enter client and location"
                  disabled={formData.shipTo.sameAsAddress}
                />
              </InputFieldWrapper>
            </InputRow>
            <InputRow>
              <InputFieldWrapper style={{ flex: 1 }}>
                <label htmlFor="shipTo-address">Address</label>
                <textarea
                  id="shipTo-address"
                  value={formData.shipTo.address}
                  onChange={(e) => handleShipToChange("address", e.target.value)}
                  placeholder="Enter address"
                  disabled={formData.shipTo.sameAsAddress}
                />
              </InputFieldWrapper>
            </InputRow>
          </SectionContainer>

          {/* Quotation Description Section */}
          <SectionContainer>
            <SectionTitle>Quotation Description</SectionTitle>
            <InputRow>
              <InputFieldWrapper style={{ flex: 1 }}>
                <label htmlFor="quotation-description">Description</label>
                <textarea
                  id="quotation-description"
                  value={formData.quotationDescription.description}
                  onChange={(e) =>
                    setValue("quotationDescription", {
                      ...formData.quotationDescription,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter quotation description"
                />
              </InputFieldWrapper>
            </InputRow>
            <InputRow>
              <InputFieldWrapper>
                <label htmlFor="vendor-name">Vendor Name (If Applicable)</label>
                <Dropdown
                  id="vendor-name"
                  placeholder="Select Vendor"
                  items={allVendors}
                  onClick={(vendor) =>
                    setValue("quotationDescription", {
                      ...formData.quotationDescription,
                      vendorName: String(vendor.id),
                    })
                  }
                />
              </InputFieldWrapper>
            </InputRow>
          </SectionContainer>

          {/* Labor Details Accordion */}
          <SectionContainer>
            <AccordionContainer>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Labor Details</Accordion.Header>
                  <Accordion.Body>
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th># of Service per Month</th>
                            <th>Rate</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.laborDetails || []).map((row) => (
                            <tr key={row.id}>
                              <td>
                                <input
                                  type="number"
                                  value={row.servicePerMonth}
                                  onChange={(e) =>
                                    updateLaborRow(row.id, "servicePerMonth", parseFloat(e.target.value) || 0)
                                  }
                                  placeholder="0"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.rate}
                                  onChange={(e) => updateLaborRow(row.id, "rate", parseFloat(e.target.value) || 0)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>${row.total.toFixed(2)}</td>
                              <td>
                                <RemoveRowButton onClick={() => removeLaborRow(row.id)}>Remove</RemoveRowButton>
                              </td>
                            </tr>
                          ))}
                          <TotalRow>
                            <td colSpan={2} style={{ textAlign: "right" }}>
                              Labour Total Amount:
                            </td>
                            <td style={{ textAlign: "right" }}>${formData.laborTotalAmount.toFixed(2)}</td>
                            <td></td>
                          </TotalRow>
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                    <AddRowButton type="button" onClick={addLaborRow}>
                      + Add New Row
                    </AddRowButton>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </AccordionContainer>
          </SectionContainer>

          {/* Service Details Accordion */}
          <SectionContainer>
            <AccordionContainer>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Service Details</Accordion.Header>
                  <Accordion.Body>
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Rate</th>
                            <th>Quantity</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.serviceDetails || []).map((row) => (
                            <tr key={row.id}>
                              <td>
                                <input
                                  type="text"
                                  value={row.service}
                                  onChange={(e) => updateServiceRow(row.id, "service", e.target.value)}
                                  placeholder="Enter service"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.rate}
                                  onChange={(e) => updateServiceRow(row.id, "rate", parseFloat(e.target.value) || 0)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.quantity}
                                  onChange={(e) =>
                                    updateServiceRow(row.id, "quantity", parseFloat(e.target.value) || 0)
                                  }
                                  placeholder="0"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>${row.total.toFixed(2)}</td>
                              <td>
                                <RemoveRowButton onClick={() => removeServiceRow(row.id)}>Remove</RemoveRowButton>
                              </td>
                            </tr>
                          ))}
                          <TotalRow>
                            <td colSpan={3} style={{ textAlign: "right" }}>
                              Service Total Amount:
                            </td>
                            <td style={{ textAlign: "right" }}>${formData.serviceTotalAmount.toFixed(2)}</td>
                            <td></td>
                          </TotalRow>
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                    <AddRowButton type="button" onClick={addServiceRow}>
                      + Add New Row
                    </AddRowButton>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </AccordionContainer>
          </SectionContainer>

          {/* Equipment Details Accordion */}
          <SectionContainer>
            <AccordionContainer>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Equipment Details</Accordion.Header>
                  <Accordion.Body>
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Equipment</th>
                            <th>Rate</th>
                            <th>Quantity</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.equipmentDetails || []).map((row) => (
                            <tr key={row.id}>
                              <td>
                                <input
                                  type="text"
                                  value={row.equipment}
                                  onChange={(e) => updateEquipmentRow(row.id, "equipment", e.target.value)}
                                  placeholder="Enter equipment"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.rate}
                                  onChange={(e) => updateEquipmentRow(row.id, "rate", parseFloat(e.target.value) || 0)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.quantity}
                                  onChange={(e) =>
                                    updateEquipmentRow(row.id, "quantity", parseFloat(e.target.value) || 0)
                                  }
                                  placeholder="0"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>${row.total.toFixed(2)}</td>
                              <td>
                                <RemoveRowButton onClick={() => removeEquipmentRow(row.id)}>Remove</RemoveRowButton>
                              </td>
                            </tr>
                          ))}
                          <TotalRow>
                            <td colSpan={3} style={{ textAlign: "right" }}>
                              Equipment Total Amount:
                            </td>
                            <td style={{ textAlign: "right" }}>${formData.equipmentTotalAmount.toFixed(2)}</td>
                            <td></td>
                          </TotalRow>
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                    <AddRowButton type="button" onClick={addEquipmentRow}>
                      + Add New Row
                    </AddRowButton>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </AccordionContainer>
          </SectionContainer>

          {/* Materials Details Accordion */}
          <SectionContainer>
            <AccordionContainer>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Materials Details</Accordion.Header>
                  <Accordion.Body>
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Material</th>
                            <th>Rate</th>
                            <th>Quantity</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.materialDetails || []).map((row) => (
                            <tr key={row.id}>
                              <td>
                                <input
                                  type="text"
                                  value={row.material}
                                  onChange={(e) => updateMaterialRow(row.id, "material", e.target.value)}
                                  placeholder="Enter material"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.rate}
                                  onChange={(e) => updateMaterialRow(row.id, "rate", parseFloat(e.target.value) || 0)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.quantity}
                                  onChange={(e) =>
                                    updateMaterialRow(row.id, "quantity", parseFloat(e.target.value) || 0)
                                  }
                                  placeholder="0"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>${row.total.toFixed(2)}</td>
                              <td>
                                <RemoveRowButton onClick={() => removeMaterialRow(row.id)}>Remove</RemoveRowButton>
                              </td>
                            </tr>
                          ))}
                          <TotalRow>
                            <td colSpan={3} style={{ textAlign: "right" }}>
                              Materials Total Amount:
                            </td>
                            <td style={{ textAlign: "right" }}>${formData.materialTotalAmount.toFixed(2)}</td>
                            <td></td>
                          </TotalRow>
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                    <AddRowButton type="button" onClick={addMaterialRow}>
                      + Add New Row
                    </AddRowButton>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </AccordionContainer>
          </SectionContainer>

          {/* Additional Fees Details Accordion */}
          <SectionContainer>
            <AccordionContainer>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Additional Fees Details</Accordion.Header>
                  <Accordion.Body>
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Type of Fee</th>
                            <th>Rate</th>
                            <th>Quantity</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.additionalFees || []).map(
                            (row: { id: string; typeOfFee: string; rate: number; quantity: number; total: number }) => (
                              <tr key={row.id}>
                                <td>
                                  <input
                                    type="text"
                                    value={row.typeOfFee}
                                    onChange={(e) => updateAdditionalFeeRow(row.id, "typeOfFee", e.target.value)}
                                    placeholder="Enter fee type"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={row.rate}
                                    onChange={(e) =>
                                      updateAdditionalFeeRow(row.id, "rate", parseFloat(e.target.value) || 0)
                                    }
                                    placeholder="0.00"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={row.quantity}
                                    onChange={(e) =>
                                      updateAdditionalFeeRow(row.id, "quantity", parseFloat(e.target.value) || 0)
                                    }
                                    placeholder="0"
                                  />
                                </td>
                                <td style={{ textAlign: "right" }}>${row.total.toFixed(2)}</td>
                                <td>
                                  <RemoveRowButton onClick={() => removeAdditionalFeeRow(row.id)}>
                                    Remove
                                  </RemoveRowButton>
                                </td>
                              </tr>
                            ),
                          )}
                          <TotalRow>
                            <td colSpan={3} style={{ textAlign: "right" }}>
                              Additional Fees Total Amount:
                            </td>
                            <td style={{ textAlign: "right" }}>${formData.additionalFeesTotalAmount.toFixed(2)}</td>
                            <td></td>
                          </TotalRow>
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                    <AddRowButton type="button" onClick={addAdditionalFeeRow}>
                      + Add New Row
                    </AddRowButton>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </AccordionContainer>
          </SectionContainer>

          {/* Quote Total Section */}
          <SectionContainer>
            <QuoteTotalContainer>
              <p>
                Quote Total Amount:
                <span>${formData.quoteTotalAmount.toFixed(2)}</span>
              </p>
            </QuoteTotalContainer>
          </SectionContainer>

          {/* Scope of Work Section */}
          <SectionContainer>
            <SectionTitle>Scope of Work</SectionTitle>
            <InputRow>
              <InputFieldWrapper style={{ flex: 1 }}>
                <label htmlFor="scope-of-work">Scope of Work</label>
                <textarea
                  id="scope-of-work"
                  value={formData.scopeOfWork.scopeOfWork}
                  onChange={(e) =>
                    setValue("scopeOfWork", {
                      ...formData.scopeOfWork,
                      scopeOfWork: e.target.value,
                    })
                  }
                  placeholder="Enter scope of work details"
                />
              </InputFieldWrapper>
            </InputRow>
            <InputRow>
              <InputFieldWrapper>
                <label htmlFor="attachment">Attachment (If Applicable)</label>
                <FileInputWrapper>
                  <input id="attachment" type="file" onChange={handleAttachmentChange} />
                  {attachmentFile && <div className="file-name">Selected file: {attachmentFile.name}</div>}
                </FileInputWrapper>
              </InputFieldWrapper>
              <InputFieldWrapper>
                <label htmlFor="markup-percentage">Mark-up (%) (If Applicable)</label>
                <input
                  id="markup-percentage"
                  type="number"
                  value={formData.scopeOfWork.markupPercentage}
                  onChange={(e) =>
                    setValue("scopeOfWork", {
                      ...formData.scopeOfWork,
                      markupPercentage: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="0.00"
                />
              </InputFieldWrapper>
            </InputRow>
          </SectionContainer>

          {/* Quote Next Step Section */}
          <SectionContainer>
            <SectionTitle>Quote Next Step</SectionTitle>
            <RadioButtonGroup>
              <RadioButtonWrapper>
                <input
                  type="radio"
                  id="dispatch-send"
                  name="quoteNextStep"
                  value="dispatch"
                  checked={formData.quoteNextStep === "dispatch"}
                  onChange={() => setValue("quoteNextStep", "dispatch")}
                />
                <label htmlFor="dispatch-send">Dispatch to send quote to client</label>
              </RadioButtonWrapper>
              <RadioButtonWrapper>
                <input
                  type="radio"
                  id="manager-send"
                  name="quoteNextStep"
                  value="manager"
                  checked={formData.quoteNextStep === "manager"}
                  onChange={() => setValue("quoteNextStep", "manager")}
                />
                <label htmlFor="manager-send">Manager to send quote to client</label>
              </RadioButtonWrapper>
            </RadioButtonGroup>
          </SectionContainer>

          {/* Submit Button Container */}
          <SubmitButtonContainer>
            <button type="submit" className="submit">
              Submit Quote
            </button>
            <button type="button" className="cancel" onClick={() => onCancel && onCancel()}>
              Cancel
            </button>
          </SubmitButtonContainer>
        </Form>
      </FormContainer>
    </LoadingContainer>
  );
};

export default RequestQuoteFormComponent;
